// src/pages/UserManagementPage.jsx
import React, { useState, useEffect } from 'react';
import './UserManagementPage.css';
import { FaUserPlus, FaTrash, FaTimes, FaEdit, FaPlus } from 'react-icons/fa';
import api from '../api';

function UserManagementPage() {
  // ===================== USERS =====================
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [showUserModal, setShowUserModal] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    role: 'regular',
    work_area_id: '', // ✅ baru
  });
  const [userErrors, setUserErrors] = useState({});

  // ===================== WORK AREAS =====================
  const [areas, setAreas] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(true);

  const [showAreaModal, setShowAreaModal] = useState(false);
  const [isEditingArea, setIsEditingArea] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState(null);

  const [areaForm, setAreaForm] = useState({
    name: '',
    code: '',
    notes: '',
    is_active: true,
  });
  const [areaErrors, setAreaErrors] = useState({});

  const normalizeList = (data) => (Array.isArray(data) ? data : (data?.data ?? []));

  useEffect(() => {
    fetchUsers();
    fetchAreas();
  }, []);

  // -------- USERS: API --------
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await api.get('/users');
      setUsers(normalizeList(res.data));
    } catch (err) {
      console.error('Gagal load users:', err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const onUserChange = (e) => {
    const { name, value } = e.target;
    setUserForm((s) => ({ ...s, [name]: value }));
    if (userErrors[name]) setUserErrors((s) => ({ ...s, [name]: '' }));
  };

  const submitUser = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!userForm.username.trim()) errs.username = 'Username wajib';
    if (!isEditingUser && !userForm.password.trim()) errs.password = 'Password wajib';
    if (Object.keys(errs).length) {
      setUserErrors(errs);
      return;
    }

    const payload = {
      username: userForm.username,
      role: userForm.role,
      work_area_id: userForm.work_area_id === '' ? null : Number(userForm.work_area_id),
      ...(userForm.password ? { password: userForm.password } : {}),
    };

    try {
      if (isEditingUser) {
        await api.put(`/users/${editingUserId}`, payload);
        alert('Data pengguna berhasil diperbarui');
      } else {
        if (!payload.password) {
          alert('Password wajib');
          return;
        }
        await api.post('/users', payload);
        alert('Pengguna baru berhasil ditambahkan');
      }
      closeUserModal();
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.errors) {
        const serverErrs = Object.fromEntries(
          Object.entries(err.response.data.errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : String(v)])
        );
        setUserErrors(serverErrs);
      } else {
        console.error('Error submit user:', err);
        alert('Terjadi kesalahan saat menyimpan pengguna');
      }
    }
  };

  const deleteUser = async (id, username) => {
    if (!window.confirm(`Hapus user '${username}'?`)) return;
    try {
      await api.delete(`/users/${id}`);
      alert('Pengguna dihapus');
      fetchUsers();
    } catch (err) {
      console.error('Gagal hapus user:', err);
      alert('Gagal hapus pengguna');
    }
  };

  const openAddUser = () => {
    setUserForm({ username: '', password: '', role: 'regular', work_area_id: '' });
    setUserErrors({});
    setIsEditingUser(false);
    setEditingUserId(null);
    setShowUserModal(true);
  };

  const openEditUser = (user) => {
    const waId = user.work_area_id ?? user.work_area?.id ?? user.workArea?.id ?? '';
    setUserForm({ username: user.username, password: '', role: user.role, work_area_id: waId === null ? '' : waId });
    setUserErrors({});
    setIsEditingUser(true);
    setEditingUserId(user.id);
    setShowUserModal(true);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setIsEditingUser(false);
    setEditingUserId(null);
    setUserErrors({});
    setUserForm({ username: '', password: '', role: 'regular', work_area_id: '' });
  };

  // -------- WORK AREAS: API --------
  const fetchAreas = async () => {
    try {
      setLoadingAreas(true);
      const res = await api.get('/workareas');
      setAreas(normalizeList(res.data));
    } catch (e) {
      console.error('Gagal memuat wilayah kerja:', e);
      setAreas([]);
    } finally {
      setLoadingAreas(false);
    }
  };

  const onAreaChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === 'checkbox' ? checked : value;
    setAreaForm((s) => ({ ...s, [name]: v }));
    if (areaErrors[name]) setAreaErrors((s) => ({ ...s, [name]: '' }));
  };

  const submitArea = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!areaForm.name.trim()) errs.name = 'Nama wilayah wajib diisi';
    if (Object.keys(errs).length) {
      setAreaErrors(errs);
      return;
    }

    try {
      if (isEditingArea) {
        await api.put(`/workareas/${editingAreaId}`, areaForm);
        alert('Wilayah kerja berhasil diperbarui');
      } else {
        await api.post('/workareas', areaForm);
        alert('Wilayah kerja berhasil ditambahkan');
      }
      closeAreaModal();
      fetchAreas();
    } catch (e2) {
      if (e2.response?.status === 422 && e2.response?.data?.errors) {
        const serverErrs = Object.fromEntries(
          Object.entries(e2.response.data.errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : String(v)])
        );
        setAreaErrors(serverErrs);
      } else {
        console.error('Gagal simpan wilayah:', e2);
        alert('Terjadi kesalahan saat menyimpan wilayah');
      }
    }
  };

  const deleteArea = async (row) => {
    if (!window.confirm(`Hapus wilayah kerja '${row.name}'?`)) return;
    try {
      await api.delete(`/workareas/${row.id}`);
      fetchAreas();
    } catch (e) {
      console.error('Gagal menghapus wilayah:', e);
      alert('Gagal menghapus wilayah kerja');
    }
  };

  const openAddArea = () => {
    setAreaForm({ name: '', code: '', notes: '', is_active: true });
    setAreaErrors({});
    setIsEditingArea(false);
    setEditingAreaId(null);
    setShowAreaModal(true);
  };

  const openEditArea = (row) => {
    setAreaForm({
      name: row.name || '',
      code: row.code || '',
      notes: row.notes || '',
      is_active: !!row.is_active,
    });
    setAreaErrors({});
    setIsEditingArea(true);
    setEditingAreaId(row.id);
    setShowAreaModal(true);
  };

  const closeAreaModal = () => {
    setShowAreaModal(false);
    setIsEditingArea(false);
    setEditingAreaId(null);
    setAreaErrors({});
    setAreaForm({ name: '', code: '', notes: '', is_active: true });
  };

  return (
    // ⬇️ Wrapper dengan scrollbar vertikal
    <div className="um-scroll" tabIndex={0}>
      <div className="user-management-container">
        {/* ===== SECTION: USERS ===== */}
        <h2>Manajemen Pengguna</h2>

        <button className="add-user-button" onClick={openAddUser}>
          <FaUserPlus style={{ marginRight: 8 }} /> Tambah Pengguna Baru
        </button>

        <div className="table-responsive">
          <table className="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Wilayah Kerja</th> {/* ✅ kolom baru */}
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loadingUsers ? (
                <tr><td colSpan={4}>Memuat...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={4}>Belum ada data</td></tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>{u.role}</td>
                    <td>{u.work_area?.name || u.workArea?.name || '-'}</td>
                    <td className="user-actions">
                      <button className="action-button edit-button" onClick={() => openEditUser(u)}>
                        <FaEdit /> Edit
                      </button>
                      <button className="action-button delete-button" onClick={() => deleteUser(u.id, u.username)}>
                        <FaTrash /> Hapus
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ===== SECTION: WORK AREAS ===== */}
        <h2 style={{ marginTop: 36 }}>Manajemen Tim Kerja & Wilayah Kerja</h2>

        <button className="add-user-button" onClick={openAddArea}>
          <FaPlus style={{ marginRight: 8 }} /> Tambah
        </button>

        <div className="table-responsive">
          <table className="users-table">
            <thead>
              <tr>
                <th>TIMKER & WILKER</th>
                <th>Kode</th>
                <th>Keterangan</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loadingAreas ? (
                <tr><td colSpan={5}>Memuat...</td></tr>
              ) : areas.length === 0 ? (
                <tr><td colSpan={5}>Belum ada data</td></tr>
              ) : (
                areas.map((r) => (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td>{r.code || '-'}</td>
                    <td style={{ whiteSpace: 'pre-wrap' }}>{r.notes || '-'}</td>
                    <td>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 12,
                          fontSize: 12,
                          background: r.is_active ? '#E7F5EE' : '#FDECEC',
                          color: r.is_active ? '#0F7B4B' : '#B42318',
                        }}
                      >
                        {r.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="user-actions">
                      <button className="action-button edit-button" onClick={() => openEditArea(r)}>
                        <FaEdit /> Edit
                      </button>
                      <button className="action-button delete-button" onClick={() => deleteArea(r)}>
                        <FaTrash /> Hapus
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ===== MODAL: USER ===== */}
        {showUserModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>{isEditingUser ? 'Edit Pengguna' : 'Tambah Pengguna Baru'}</h3>
                <button className="close-modal-button" onClick={closeUserModal}>
                  <FaTimes />
                </button>
              </div>
              <div className="modal-body">
                <form className="user-form" onSubmit={submitUser}>
                  <div className="form-group">
                    <label htmlFor="username">Username:</label>
                    <input
                      id="username"
                      name="username"
                      value={userForm.username}
                      onChange={onUserChange}
                      className={userErrors.username ? 'input-error' : ''}
                      type="text"
                    />
                    {userErrors.username && <span className="error-message">{userErrors.username}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">
                      Password {isEditingUser && '(kosongkan jika tidak diganti)'}:
                    </label>
                    <input
                      id="password"
                      name="password"
                      value={userForm.password}
                      onChange={onUserChange}
                      className={userErrors.password ? 'input-error' : ''}
                      type="password"
                    />
                    {userErrors.password && <span className="error-message">{userErrors.password}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="role">Role:</label>
                    <select id="role" name="role" value={userForm.role} onChange={onUserChange}>
                      <option value="regular">Regular User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  {/* ✅ Dropdown Wilayah Kerja */}
                  <div className="form-group">
                    <label htmlFor="work_area_id">Wilayah Kerja:</label>
                    <select
                      id="work_area_id"
                      name="work_area_id"
                      value={userForm.work_area_id}
                      onChange={onUserChange}
                    >
                      <option value="">— Tanpa Wilayah —</option>
                      {(areas || []).map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                    {userErrors.work_area_id && (
                      <span className="error-message">{userErrors.work_area_id}</span>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="submit-button">
                      {isEditingUser ? 'Simpan Perubahan' : 'Tambah Pengguna'}
                    </button>
                    <button type="button" className="cancel-button" onClick={closeUserModal}>
                      Batal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* ===== MODAL: WORK AREA ===== */}
        {showAreaModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: 560 }}>
              <div className="modal-header">
                <h3>{isEditingArea ? 'Edit Tim Kerja & Wilayah Kerja' : 'Tambah Wilayah Kerja'}</h3>
                <button className="close-modal-button" onClick={closeAreaModal}>
                  <FaTimes />
                </button>
              </div>
              <div className="modal-body">
                <form className="user-form" onSubmit={submitArea}>
                  <div className="form-group">
                    <label htmlFor="wa-name">Nama TIMKER/WILKER:</label>
                    <input
                      id="wa-name"
                      name="name"
                      value={areaForm.name}
                      onChange={onAreaChange}
                      className={areaErrors.name ? 'input-error' : ''}
                      type="text"
                    />
                    {areaErrors.name && <span className="error-message">{areaErrors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="wa-code">Kode (opsional):</label>
                    <input
                      id="wa-code"
                      name="code"
                      value={areaForm.code}
                      onChange={onAreaChange}
                      className={areaErrors.code ? 'input-error' : ''}
                      type="text"
                    />
                    {areaErrors.code && <span className="error-message">{areaErrors.code}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="wa-notes">Keterangan (opsional):</label>
                    <textarea
                      id="wa-notes"
                      name="notes"
                      rows={3}
                      value={areaForm.notes}
                      onChange={onAreaChange}
                      placeholder="Catatan tambahan…"
                    />
                    {areaErrors.notes && <span className="error-message">{areaErrors.notes}</span>}
                  </div>

                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      id="wa-active"
                      name="is_active"
                      type="checkbox"
                      checked={!!areaForm.is_active}
                      onChange={onAreaChange}
                      style={{ width: 18, height: 18 }}
                    />
                    <label htmlFor="wa-active" style={{ margin: 0 }}>Aktif</label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="submit-button">
                      {isEditingArea ? 'Simpan Perubahan' : 'Tambah Wilayah'}
                    </button>
                    <button type="button" className="cancel-button" onClick={closeAreaModal}>
                      Batal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagementPage;
